// Заброшенные замки: серьёзный гарнизон (по силам игрокам с замком 15–20 уровня и выше), стена и
// случайная часть уже построенных зданий (30–40%), которая достаётся захватчику.
import type { Balance } from './balance.js';
import { buildingMaxLevel } from './formulas.js';
import { XorShift128 } from './rng.js';
import type { BuildingType } from './types.js';

/** Постоянные участки зданий: индекс в списке — номер участка в замке и на поле ресурсов. */
export const CASTLE_PLOT_TYPES = [
  'barracks',
  'archery_range',
  'stable',
  'forge',
  'headquarters',
  'hospital',
  'academy',
  'hero_temple',
  'market',
  'embassy',
  'tavern',
  'warehouse',
  'port',
  'shipyard',
] as const;
export const FIELD_PLOT_TYPES = ['farm', 'sawmill', 'quarry', 'iron_mine', 'silver_mine'] as const;

/** Гарнизон заброшенного замка уровня level: войска 2-го ранга растут с уровнем, с eliteFromLevel — 3-й ранг. */
export function abandonedCastleGarrison(b: Balance, level: number): Record<string, number> {
  const a = b.events.abandoned;
  const k = a.garrisonGrowth ** Math.max(0, level - a.levelMin);
  const g: Record<string, number> = {};
  for (const [unit, n] of Object.entries(a.garrisonBase)) g[unit] = Math.round(n * k);
  if (level >= a.eliteFromLevel)
    for (const [unit, per] of Object.entries(a.garrisonElitePerLevel))
      g[unit] = (g[unit] ?? 0) + Math.round(per * (level - a.eliteFromLevel + 1));
  return g;
}

export function abandonedWallLevel(b: Balance, level: number): number {
  return Math.min(b.events.abandoned.wallMax, b.events.abandoned.wallBase + level);
}

/**
 * Что уже построено в заброшенном замке: случайные 30–40% доступных по уровню зданий (детерминированно по seed),
 * уровни — от половины уровня замка до уровня замка.
 */
export function abandonedBuildings(
  b: Balance,
  level: number,
  seed: string,
): { type: BuildingType; zone: 'castle' | 'field'; plot: number; level: number }[] {
  const rng = new XorShift128(`abandoned-buildings:${seed}`);
  const [minShare, maxShare] = b.events.abandoned.buildingsShare;
  const candidates = [
    ...CASTLE_PLOT_TYPES.map((type, plot) => ({ type: type as BuildingType, zone: 'castle' as const, plot })),
    ...FIELD_PLOT_TYPES.map((type, plot) => ({ type: type as BuildingType, zone: 'field' as const, plot })),
  ].filter((c) => (b.buildings.list[c.type]?.requiresTownHall ?? 99) <= level);
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [candidates[i], candidates[j]] = [candidates[j]!, candidates[i]!];
  }
  const share = minShare + (maxShare - minShare) * rng.next();
  const count = Math.max(1, Math.round(candidates.length * share));
  return candidates.slice(0, count).map((c) => ({
    ...c,
    level: Math.min(buildingMaxLevel(b.buildings, c.type), rng.int(Math.max(1, Math.ceil(level / 2)), level)),
  }));
}
